import {
    Button,
    Heading,
    Icon,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Stack,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaCheck, FaEdit, FaPlus, FaSync } from "react-icons/fa";
import ImportCSVButton from "../../components/Buttons/ImportCSVButton";
import { RiSurveyFill } from "react-icons/ri";
import { useSelector } from "react-redux";
import { GET, POST } from "../../utils/ApiRequestProvider";

export default function ChartOfAccounts() {
    const business = useSelector((state: any) => state.selections.value?.business);
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [items, setItems] = useState<Array<any>>([]);
    const [loading, setLoading] = useState(true);

    const [form, setForm] = useState<any>({
        code: '',
        category: '',
        type: '',
        status: ''
    });

    async function Save() {
        try {
            setLoading(true);

            const response = await POST(
                '/api/chartofaccounts',
                {
                    ...form,
                    businessId: business.id
                }
            );
            toast({
                status: response.status,
                title: response.message,
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });

            setLoading(false);

            if (response.status === 'success') {
                FetchList();
                setForm({
                    code: '',
                    category: '',
                    type: '',
                    status: ''
                });
                onClose();
            }
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
    }

    async function Edit(data: any) {
        try {
            setForm(data);
            onOpen();
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
    }

    async function CloseModel() {
        setForm({
            code: '',
            category: '',
            type: '',
            status: ''
        });
        onClose();
    }

    async function RefreshPage() {
        setLoading(true);
        setTimeout(() => {FetchList();setLoading(false)}, 1000);
    }

    useEffect(() => {
        FetchList();
        setTimeout(() => {setLoading(false)}, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [business]);

    async function FetchList() {
        try {
            if (business) {
                const res = await GET(`/api/chartofaccounts/${business.id}`);
                setItems(res.data);
            }
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
    }

    return (
        <Stack w={'full'} justify={"center"} align={"center"} spacing={6}>
            <Stack
                direction={{ base: 'column', md: 'column', lg: 'column', xl: 'row' }}
                justify={'space-between'}
                boxShadow={'base'}
                rounded={'xl'}
                w={'98%'}
                p={4}
                spacing={{ base: 6, md: 6, lg: 6, xl: 0 }}
                align={'center'}
            >
                <Stack
                    align={'flex-start'}
                    justify={'start'}
                    spacing={4}
                >
                    <Stack direction={'row'}>
                        <Icon
                            as={RiSurveyFill}
                            fontSize={'5xl'}
                            color={'primary.400'}
                        />
                        <Stack spacing={1}>
                            <Heading fontSize={'xl'}>Chart of Accounts</Heading>
                            <Text fontSize={'sm'}>Here you can see all your chart of accounts imported from online accounting softwares</Text>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack align={{ lg: 'flex-start', xl: 'flex-end' }} justify={'end'} spacing={4}>
                    <Stack direction={'row'}>
                        <Button
                            size={'sm'}
                            leftIcon={<FaPlus />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={onOpen}
                        >
                            Add Chart of Account
                        </Button>
                        <ImportCSVButton
                            endpoint={business ? `/api/chartofaccounts/${business.id}/import` : '/api/chartofaccounts/import'}
                            label={'Import Chart of Account'}
                            onImported={() => FetchList()}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                        />
                    </Stack>
                    <Stack direction={'row'}>
                        <Button
                            size={'sm'}
                            leftIcon={<FaSync />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={RefreshPage}
                            isLoading={loading}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Stack>
            </Stack>

            <Stack boxShadow={'base'} rounded={'xl'} w={'98%'} p={4} spacing={4}>
                <TableContainer h={'sm'} overflowY={'scroll'}>
                    <Table size='sm'>
                        <Thead position={'sticky'} top={'0%'} zIndex={1} h={'50px'} bg={'primary.400'} alignItems={'flex-start'}>
                            <Tr>
                                <Th
                                    color={'white'}
                                >
                                    Account Code
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Account Name
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Account Type
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Status
                                </Th>
                                <Th
                                    color={'white'}
                                    textAlign={'center'}
                                >
                                    Action
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {
                                loading ? (
                                    <Tr>
                                        <Td textAlign={'center'} colSpan={5}>Loading...</Td>
                                    </Tr>
                                ) :
                                items?.length === 0 ? (
                                    <Tr>
                                        <Td textAlign={'center'} colSpan={5}>No Data Found</Td>
                                    </Tr>
                                ) :
                                items.map(item => (
                                    <Tr>
                                        <Td>{item.code}</Td>
                                        <Td>{item.category}</Td>
                                        <Td>{item.type}</Td>
                                        <Td>{item.status}</Td>
                                        <Td as={Stack} direction={'row'} justify={'center'} spacing={4}>
                                            <IconButton
                                                aria-label="Edit Contact"
                                                icon={<FaEdit />}
                                                size={'md'}
                                                bg={'transparent'}
                                                color={'primary.400'}
                                                rounded={'full'}
                                                onClick={() => Edit(item)}
                                            />
                                        </Td>
                                    </Tr>
                                ))
                            }
                        </Tbody>
                    </Table>
                </TableContainer>
            </Stack>

            <Modal
                isCentered
                size={'3xl'}
                isOpen={isOpen}
                onClose={onClose}
            >
                <ModalOverlay
                    bg='blackAlpha.300'
                    backdropFilter='blur(10px)'
                />
                <ModalContent>
                    <ModalHeader
                        as={Stack}
                        direction={'row'}
                        borderBottom={'2px'}
                        borderBottomColor={'gray.300'}
                    >
                        <Icon
                            as={RiSurveyFill}
                            fontSize={'5xl'}
                            color={'primary.400'}
                        />
                        <Stack spacing={0}>
                            <Heading fontSize={'xl'}>Add New Chart of Account</Heading>
                            <Text fontSize={'sm'}>Create a new chart of account from here</Text>
                        </Stack>
                    </ModalHeader>
                    <ModalBody as={Stack} direction={'row'} spacing={4} py={8}>
                        <Stack spacing={4} w={'full'}>
                            <Input
                                w={'full'}
                                type={'number'}
                                size={'md'}
                                placeholder={'Account Code'}
                                value={form.code}
                                onChange={(e: any) => setForm({ ...form, code: e.target.value })}
                            />
                            <Input
                                w={'full'}
                                type={'text'}
                                size={'md'}
                                placeholder={'Account Name'}
                                value={form.category}
                                onChange={(e: any) => {
                                    setForm({
                                        ...form,
                                        category: e.target.value
                                    })
                                }}
                            />
                            <Select
                                w={'full'}
                                placeholder={'Account Type'}
                                value={form.type}
                                onChange={(e: any) =>
                                    setForm({ ...form, type: e.target.value })
                                }
                            >
                                <option value={'Assets'}>Assets</option>
                                <option value={'Liabilities'}>Liabilities</option>
                                <option value={'Expenses and Costs'}>Expenses and Costs</option>
                                <option value={'Equity'}>Equity</option>
                                <option value={'Income or Revenue'}>Income or Revenue</option>
                            </Select>
                            <Select
                                w={'full'}
                                placeholder={'Status'}
                                value={form.status}
                                onChange={(e: any) =>
                                    setForm({ ...form, status: e.target.value })
                                }
                            >
                                <option value={'Active'}>Active</option>
                                <option value={'Inactive'}>Inactive</option>
                            </Select>
                        </Stack>
                    </ModalBody>
                    <ModalFooter
                        as={Stack}
                        direction={'row'}
                        spacing={4}
                        borderTop={'2px'}
                        borderTopColor={'gray.300'}
                    >
                        <Button
                            w={'full'}
                            onClick={CloseModel}
                        >
                            Close
                        </Button>
                        <Button
                            w={'full'}
                            onClick={Save}
                            leftIcon={<FaCheck />}
                            colorScheme={'primary'}
                            isLoading={loading}
                        >
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Stack >
    )
}